import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FaCheckCircle, FaExclamationTriangle, FaMapMarkerAlt, FaDollarSign, FaUserTie, FaShieldAlt, FaFileContract } from "react-icons/fa";
import styles from "./Pages.module.css";

function HowToChooseIT() {
  return (
    <section className={styles.pageSection}>
      <Helmet>
        <title>How to Choose an IT Support Provider | Tharpe Industries</title>
        <meta
          name="description"
          content="Choosing IT support is a critical decision. Learn what to look for in an IT company that serves Shreveport-Bossier small businesses. No sales pitch, no jargon."
        />
        <meta
          name="keywords"
          content="choose IT support provider, hiring IT company, small business IT support, IT vendor evaluation, managed IT services"
        />
        <link rel="canonical" href="https://tharpeindustries.com/how-to-choose-it-support" />
      </Helmet>

      <h2>How to Choose an IT Support Provider for Your Small Business</h2>

      <p>
        Most small business owners don't think about IT support until something breaks. Then they panic, call the first person who answers, and hope for the best. That's how you end up with the wrong provider for your business.
      </p>

      <p>
        Hiring the wrong IT company doesn't just cost money, it costs time, frustration, and lost productivity. Here's how to make a better choice.
      </p>

      <h3>Five Things to Look For</h3>

      <div style={{ marginBottom: "1.5rem" }}>
        <h4 style={{ marginBottom: "0.5rem", color: "var(--primary)" }}>
          <FaMapMarkerAlt style={{ marginRight: "0.5rem", color: "var(--accent-orange)" }} aria-hidden="true" />
          Someone Local (Or At Least Reachable)
        </h4>
        <p style={{ marginLeft: "1.75rem" }}>
          When something breaks, you want to pick up the phone and reach an actual person. Not a call center. Not a ticket queue that takes 48 hours to respond. Someone who knows your area, knows you, and can help now. If they're based in your city, that's ideal. If not, they should at least be responsive.
        </p>
      </div>

      <div style={{ marginBottom: "1.5rem" }}>
        <h4 style={{ marginBottom: "0.5rem", color: "var(--primary)" }}>
          <FaDollarSign style={{ marginRight: "0.5rem", color: "var(--accent-orange)" }} aria-hidden="true" />
          Transparent Pricing
        </h4>
        <p style={{ marginLeft: "1.75rem" }}>
          You should know what you're paying before you sign anything. Not "we'll quote you after we assess your systems." That's how you end up surprised. Good providers give you clear pricing upfront: hourly rates, service plan costs, what's included, what costs extra. Simple.
        </p>
      </div>

      <div style={{ marginBottom: "1.5rem" }}>
        <h4 style={{ marginBottom: "0.5rem", color: "var(--primary)" }}>
          <FaUserTie style={{ marginRight: "0.5rem", color: "var(--accent-orange)" }} aria-hidden="true" />
          A Dedicated Contact
        </h4>
        <p style={{ marginLeft: "1.75rem" }}>
          You should know who to call. Not "someone from the team will get back to you." Ask directly: "Who will I talk to when something breaks?" If they can't answer that clearly, move on. You want one person (or a small team) who knows your business.
        </p>
      </div>

      <div style={{ marginBottom: "1.5rem" }}>
        <h4 style={{ marginBottom: "0.5rem", color: "var(--primary)" }}>
          <FaShieldAlt style={{ marginRight: "0.5rem", color: "var(--accent-orange)" }} aria-hidden="true" />
          Proactive Approach
        </h4>
        <p style={{ marginLeft: "1.75rem" }}>
          Do they just fix problems when you call? Or do they prevent problems by monitoring, patching, and updating proactively? Good IT support catches issues before they become crises. Ask them: "How do you prevent downtime?" Their answer matters.
        </p>
      </div>

      <div>
        <h4 style={{ marginBottom: "0.5rem", color: "var(--primary)" }}>
          <FaCheckCircle style={{ marginRight: "0.5rem", color: "var(--accent-orange)" }} aria-hidden="true" />
          References or Reviews from Businesses Like Yours
        </h4>
        <p style={{ marginLeft: "1.75rem" }}>
          Ask for references. Call them. Ask: "Have they been reliable?" "Do they respond quickly?" "Would you recommend them?" If they won't give you references, that's a red flag. Also check Google reviews and local business directories.
        </p>
      </div>

      <h3>Three Red Flags to Avoid</h3>

      <div style={{ marginBottom: "1.5rem", padding: "1rem", backgroundColor: "rgba(239, 68, 68, 0.05)", borderLeft: "4px solid #ef4444", borderRadius: "0.5rem" }}>
        <h4 style={{ marginTop: 0, marginBottom: "0.5rem", color: "#ef4444" }}>
          <FaExclamationTriangle aria-hidden="true" /> Vague Pricing
        </h4>
        <p style={{ margin: "0 0 0 1.75rem" }}>
          "We'll quote you after we assess your systems." This is stalling. You should get a price conversation upfront, even if it's rough. If they won't talk numbers until after they've already started work, they're hiding something.
        </p>
      </div>

      <div style={{ marginBottom: "1.5rem", padding: "1rem", backgroundColor: "rgba(239, 68, 68, 0.05)", borderLeft: "4px solid #ef4444", borderRadius: "0.5rem" }}>
        <h4 style={{ marginTop: 0, marginBottom: "0.5rem", color: "#ef4444" }}>
          <FaFileContract style={{ marginRight: "0.5rem" }} aria-hidden="true" /> Long-Term Contracts with No Exit Clause
        </h4>
        <p style={{ margin: "0 0 0 1.75rem" }}>
          Don't sign anything that locks you in for 2–3 years with early termination penalties. You should be able to leave if the relationship isn't working. A good provider isn't afraid of that.
        </p>
      </div>

      <div style={{ padding: "1rem", backgroundColor: "rgba(239, 68, 68, 0.05)", borderLeft: "4px solid #ef4444", borderRadius: "0.5rem" }}>
        <h4 style={{ marginTop: 0, marginBottom: "0.5rem", color: "#ef4444" }}>
          <FaUserTie style={{ marginRight: "0.5rem" }} aria-hidden="true" /> No Clear Answer to "Who Will I Talk To?"
        </h4>
        <p style={{ margin: "0 0 0 1.75rem" }}>
          If they can't tell you who your contact is, run. You'll spend all your time on hold or in email chains. You want to know exactly who to call.
        </p>
      </div>

      <h3>The Bottom Line</h3>
      <p>
        You're hiring someone to help manage the technology that runs your business. Don't rush it. Ask questions. Get references. Make sure they're local or responsive, transparent about pricing, and genuinely committed to your success, not just your next invoice.
      </p>

      <p>
        If you want to see how Tharpe Industries handles these principles, the IT Health Check is the lowest-risk way to start. You'll get a clear picture of your setup and how I work. No obligation to continue if it's not a fit.
      </p>

      <div className={styles.relatedPages}>
        <p className={styles.relatedPagesHeading}>IT support for specific industries in Shreveport-Bossier:</p>
        <div className={styles.relatedPageLinks}>
          <Link to="/it-support-dental-offices">Dental Practices</Link>
          <Link to="/it-support-law-firms">Law Firms</Link>
          <Link to="/it-support-accounting-firms">Accounting Firms</Link>
          <Link to="/it-support-contractors">Contractors & Trades</Link>
          <Link to="/it-support-restaurants">Restaurants</Link>
        </div>
      </div>

      <div className={styles.ctaContainer}>
        <p><strong>Start with the $149 IT Health Check</strong></p>
        <p className={styles.emergencyText}>See how we evaluate your setup and provide clear recommendations without pressure.</p>
        <Link to="/it-health-check" className={styles.ctaButton}>Book the IT Health Check</Link>
      </div>

      <div className={styles.secondaryCta}>
        <p>Have questions about choosing a provider? <Link to="/contact-us">Send us a message</Link> and we'll help you think it through.</p>
      </div>

      <div className={styles.resourceLinks}>
        <p style={{ fontSize: "0.9rem", color: "var(--neutral-600)", marginBottom: "0.5rem" }}>
          Related: <Link to="/ransomware-protection">Ransomware Protection Guide</Link> · <Link to="/managed-it-vs-break-fix">Managed IT vs. Break-Fix</Link>
        </p>
      </div>
    </section>
  );
}

export default HowToChooseIT;
